/**
 * React hook for connecting as a writer to a Polygraf transcript document.
 * Thin adapter over {@link PolygrafConnection}.
 */

import {
	type DocumentOperation,
	type DocumentState,
	MessageType,
	type Paragraph,
	PolygrafConnection,
	RECONNECT_RETRIES_EXHAUSTED,
	resolveWsUrl,
	SubscriberCountMessageSchema,
	type TextSegment,
	UpdateOperation,
} from '@polygraf/shared'
import { useCallback, useEffect, useRef, useState } from 'react'

const PING_INTERVAL_MS = 30_000
const STREAMING_DEBOUNCE_MS = 50

/**
 * Server error codes (websocket/server.ts `sendError`) that mean the writer's
 * auth session is missing or expired — logging in again is the fix. Other
 * codes (FORBIDDEN, WRITER_EXISTS, …) are not solved by
 * re-authentication and stay classified as generic.
 */
const AUTH_ERROR_CODES = new Set(['AUTH_REQUIRED', 'AUTH_INVALID'])

/**
 * Classification of the last connection error:
 * - `auth` — server rejected `writer_register` with AUTH_REQUIRED/AUTH_INVALID;
 *   reconnecting with the same token cannot help, the user must log in again.
 * - `exhausted` — automatic reconnect attempts ran out; a manual reconnect
 *   restarts the retry cycle.
 * - `generic` — any other error (transient transport errors during automatic
 *   retries, server-side validation errors, …).
 */
export type WriterErrorKind = 'auth' | 'exhausted' | 'generic'

interface UseWriterSocketOptions {
	documentId: string
	title: string
	token?: string
	onError?: (error: Error) => void
}

interface UseWriterSocketReturn {
	isConnected: boolean
	isConnecting: boolean
	isRegistered: boolean
	error: string | null
	/**
	 * Classification of `error` — `null` while there is no error. Lets the UI
	 * route the user to the right recovery action (re-login vs. reconnect).
	 */
	errorKind: WriterErrorKind | null
	document: DocumentState | null
	/**
	 * Current number of subscribers (viewers) attached to the writer's document
	 * on the connected server instance. Pushed by the server on every
	 * subscribe / unsubscribe / disconnect and seeded once on writer registration.
	 * `null` until the first message lands so callers can distinguish
	 * "unknown" from "zero viewers".
	 */
	subscriberCount: number | null
	reconnect: () => void
	insertParagraph: (text: string, id?: string) => void
	replaceParagraph: (paragraphId: string, segments: TextSegment[]) => void
	deleteParagraph: (paragraphId: string) => void
	/**
	 * Delete several paragraphs in ONE writer_update. Sending them as separate
	 * messages raced on the server (each read-modify-wrote the stored doc and
	 * the last write won), so a reconnecting viewer got deleted text back.
	 */
	deleteParagraphs: (paragraphIds: string[]) => void
	/** Drop a debounced streaming frame that has not been sent yet. */
	cancelPendingStream: () => void
	streamText: (paragraphId: string, text: string, isNew: boolean) => void
}

export function useWriterSocket({
	documentId,
	title,
	token,
	onError,
}: UseWriterSocketOptions): UseWriterSocketReturn {
	const [isConnected, setIsConnected] = useState(false)
	const [isConnecting, setIsConnecting] = useState(false)
	const [isRegistered, setIsRegistered] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [errorKind, setErrorKind] = useState<WriterErrorKind | null>(null)
	const [document, setDocument] = useState<DocumentState | null>(null)
	const [subscriberCount, setSubscriberCount] = useState<number | null>(null)

	const connectionRef = useRef<PolygrafConnection | null>(null)
	const streamDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const pendingStreamRef = useRef<{ paragraphId: string; text: string; isNew: boolean } | null>(
		null,
	)

	const onErrorRef = useRef(onError)
	useEffect(() => {
		onErrorRef.current = onError
	}, [onError])

	useEffect(() => {
		if (!documentId || !title || !token) return

		setIsConnecting(true)
		setError(null)
		setErrorKind(null)

		const conn = new PolygrafConnection(
			{
				url: resolveWsUrl({
					envWsUrl: import.meta.env.VITE_WS_URL,
					envApiUrl: import.meta.env.VITE_API_URL,
				}),
				pingInterval: PING_INTERVAL_MS,
			},
			{
				onOpen: () => {
					setIsConnected(true)
					setIsConnecting(false)
					// Transport recovered — clear stale errors. If registration
					// still fails (e.g. expired token) the server's error message
					// re-arrives right after this and re-populates both fields.
					setError(null)
					setErrorKind(null)
					const registerMsg: Record<string, unknown> = {
						type: 'writer_register',
						documentId,
						title,
					}
					if (token) registerMsg.token = token
					conn.send(registerMsg)
				},
				onClose: () => {
					setIsConnected(false)
					setIsConnecting(false)
					setIsRegistered(false)
					setSubscriberCount(null)
				},
				onError: (err) => {
					setError(err.message)
					setErrorKind(err.message === RECONNECT_RETRIES_EXHAUSTED ? 'exhausted' : 'generic')
					setIsConnecting(false)
					onErrorRef.current?.(err)
				},
			},
		)
		connectionRef.current = conn

		conn.on('writer_registered', (data) => {
			// Servers since 25. 9. 2026 send the stored document along; older ones
			// do not, and then the writer starts empty as before.
			if (data.document) setDocument(data.document as DocumentState)
			setIsRegistered(true)
		})
		conn.on('document_state', (data) => setDocument(data.document as DocumentState))
		conn.on(MessageType.SUBSCRIBER_COUNT, (data) => {
			// Validate at the trust boundary — a malformed server payload
			// shouldn't silently drift the writer UI's count display.
			const parsed = SubscriberCountMessageSchema.safeParse(data)
			if (!parsed.success) return
			if (parsed.data.documentId !== documentId) return
			setSubscriberCount(parsed.data.count)
		})
		conn.on('connected', () => {
			/* welcome — no-op */
		})
		conn.on('error', (data) => {
			const message = typeof data.message === 'string' ? data.message : 'unknown error'
			const code = typeof data.code === 'string' ? data.code : ''
			setError(message)
			setErrorKind(AUTH_ERROR_CODES.has(code) ? 'auth' : 'generic')
			onErrorRef.current?.(new Error(message))
		})

		return () => {
			conn.disconnect()
			connectionRef.current = null
			// disconnect() detaches onClose before closing, so reset here what
			// onClose would have: otherwise the next document starts out
			// "registered" and holding the previous document's mirror.
			setIsConnected(false)
			setIsRegistered(false)
			setSubscriberCount(null)
			setDocument(null)
			if (streamDebounceRef.current) {
				clearTimeout(streamDebounceRef.current)
				streamDebounceRef.current = null
			}
		}
	}, [documentId, title, token])

	const sendOperation = useCallback(
		(operations: DocumentOperation[]) => {
			connectionRef.current?.send({ type: 'writer_update', documentId, operations })
		},
		[documentId],
	)

	const insertParagraph = useCallback(
		(text: string, id?: string) => {
			const paragraphId = id ?? `p-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
			const newParagraph: Paragraph = {
				id: paragraphId,
				segments: [{ text }],
				timestamp: Date.now(),
			}
			sendOperation([{ op: UpdateOperation.INSERT, newParagraph }])
			setDocument((prev) =>
				prev
					? {
							...prev,
							paragraphs: [...prev.paragraphs, newParagraph],
							version: prev.version + 1,
							lastModified: Date.now(),
						}
					: prev,
			)
		},
		[sendOperation],
	)

	const replaceParagraph = useCallback(
		(paragraphId: string, segments: TextSegment[]) => {
			sendOperation([{ op: UpdateOperation.REPLACE, paragraphId, content: segments }])
			setDocument((prev) =>
				prev
					? {
							...prev,
							paragraphs: prev.paragraphs.map((p) =>
								p.id === paragraphId ? { ...p, segments } : p,
							),
							version: prev.version + 1,
							lastModified: Date.now(),
						}
					: prev,
			)
		},
		[sendOperation],
	)

	const deleteParagraph = useCallback(
		(paragraphId: string) => {
			sendOperation([{ op: UpdateOperation.DELETE, paragraphId }])
			setDocument((prev) =>
				prev
					? {
							...prev,
							paragraphs: prev.paragraphs.filter((p) => p.id !== paragraphId),
							version: prev.version + 1,
							lastModified: Date.now(),
						}
					: prev,
			)
		},
		[sendOperation],
	)

	const deleteParagraphs = useCallback(
		(paragraphIds: string[]) => {
			if (paragraphIds.length === 0) return
			sendOperation(
				paragraphIds.map((paragraphId) => ({ op: UpdateOperation.DELETE, paragraphId })),
			)
			const gone = new Set(paragraphIds)
			setDocument((prev) =>
				prev
					? {
							...prev,
							paragraphs: prev.paragraphs.filter((p) => !gone.has(p.id)),
							version: prev.version + 1,
							lastModified: Date.now(),
						}
					: prev,
			)
		},
		[sendOperation],
	)

	const cancelPendingStream = useCallback(() => {
		if (streamDebounceRef.current) {
			clearTimeout(streamDebounceRef.current)
			streamDebounceRef.current = null
		}
		pendingStreamRef.current = null
	}, [])

	const streamText = useCallback(
		(paragraphId: string, text: string, isNew: boolean) => {
			pendingStreamRef.current = { paragraphId, text, isNew }
			if (streamDebounceRef.current) clearTimeout(streamDebounceRef.current)
			streamDebounceRef.current = setTimeout(() => {
				const pending = pendingStreamRef.current
				if (!pending) return
				connectionRef.current?.send({
					type: 'streaming_text',
					documentId,
					paragraphId: pending.paragraphId,
					text: pending.text,
					cursorPosition: pending.text.length,
					isNewParagraph: pending.isNew,
				})
				pendingStreamRef.current = null
			}, STREAMING_DEBOUNCE_MS)
		},
		[documentId],
	)

	const reconnect = useCallback(() => {
		if (!connectionRef.current) return
		// Manual retry: clear the previous failure and show progress immediately.
		// Failures during the fresh attempt cycle re-populate error/errorKind.
		setError(null)
		setErrorKind(null)
		setIsConnecting(true)
		connectionRef.current.reconnect()
	}, [])

	return {
		isConnected,
		isConnecting,
		isRegistered,
		error,
		errorKind,
		document,
		subscriberCount,
		reconnect,
		insertParagraph,
		replaceParagraph,
		deleteParagraph,
		deleteParagraphs,
		cancelPendingStream,
		streamText,
	}
}
